import {
  enableActivityTracking,
  newTracker,
  setCustomUrl,
  setReferrerUrl,
  setUserId,
  trackPageView as snowplowTrackPageView,
  trackSelfDescribingEvent,
} from "@snowplow/browser-tracker";

const TRACKER_NAMESPACE = "gradient-nexus";
const SCHEMA_VERSION = "1-0-0";

let isInitialized = false;

const runSafely = (action) => {
  try {
    action();
  } catch (error) {
    console.warn("Analytics call failed:", error);
  }
};

/** Starts the Snowplow browser tracker. No-op if already initialized or if
 * REACT_APP_SNOWPLOW_COLLECTOR is unset. Call once, at app startup. */
export const initAnalytics = () => {
  if (isInitialized) {
    return;
  }
  const endpoint = process.env.REACT_APP_SNOWPLOW_COLLECTOR;
  if (!endpoint) {
    return;
  }
  runSafely(() => {
    newTracker(TRACKER_NAMESPACE, endpoint, {
      appId: process.env.REACT_APP_SNOWPLOW_APP_ID,
    });
    enableActivityTracking({ minimumVisitLength: 30, heartbeatDelay: 30 });
    isInitialized = true;
  });
};

/** Associates subsequent tracked events with the signed-in user. Call on login. */
export const setAnalyticsUserId = (unid) => {
  if (!isInitialized || !unid) {
    return;
  }
  runSafely(() => setUserId(unid));
};

/** Disassociates subsequent tracked events from any user. Call on logout or a 401. */
export const clearAnalyticsUserId = () => {
  if (!isInitialized) {
    return;
  }
  runSafely(() => setUserId(null));
};

/** Records a page view for the current route. Pass the previous route's URL (as given
 * to this function on the prior call) so it is recorded as the referrer. Call on every
 * route change, including the first. */
export const trackPageView = (previousPageUrl) => {
  if (!isInitialized) {
    return;
  }
  runSafely(() => {
    setCustomUrl(`${window.location.origin}${window.location.pathname}`);
    if (previousPageUrl) {
      setReferrerUrl(previousPageUrl);
    }
    snowplowTrackPageView();
  });
};

/** Tracks a self-describing event against an `iglu:com.gradient/<schemaName>/jsonschema/1-0-0`
 * schema (see `analyticsEvents.js` for the registered names and their payload shapes). */
export const trackEvent = (schemaName, data = {}) => {
  if (!isInitialized) {
    return;
  }
  runSafely(() => {
    setCustomUrl(`${window.location.origin}${window.location.pathname}`);
    trackSelfDescribingEvent({
      event: {
        schema: `iglu:com.gradient/${schemaName}/jsonschema/${SCHEMA_VERSION}`,
        data,
      },
    });
  });
};
